/**
 * Hala Admin - Multi-Sheet Excel Import Modal
 * Production-grade Excel workbook import with validation preview
 */

import React, { useState, useCallback } from 'react';
import {
  Upload, FileSpreadsheet, CheckCircle, AlertTriangle, X, ArrowLeft, ArrowRight,
  Loader2, Download, RefreshCw, AlertCircle, Check, Info, ChevronDown, ChevronRight,
  Trash2, Plus, Eye, FileText
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api/catalog';

// Design tokens
const COLORS = {
  primary: '#0C4A6E',
  gold: '#D4AF37',
  success: '#059669',
  error: '#DC2626',
  warning: '#D97706',
  info: '#2563EB',
  text: '#1E293B',
  textMuted: '#64748B',
  border: '#E2E8F0',
  bg: '#F8FAFC',
  white: '#FFFFFF',
};

// Sheet status colors
const STATUS_COLORS = {
  ready: { bg: '#DCFCE7', color: '#166534', icon: CheckCircle },
  warning: { bg: '#FEF3C7', color: '#92400E', icon: AlertTriangle },
  error: { bg: '#FEE2E2', color: '#991B1B', icon: AlertCircle },
  missing: { bg: '#F1F5F9', color: '#64748B', icon: Info },
  detected: { bg: '#DBEAFE', color: '#1E40AF', icon: FileSpreadsheet },
};

// Sheet name translations
const SHEET_LABELS = {
  programs: 'البرامج',
  categories: 'التصنيفات',
  cities: 'المدن',
  branches: 'الفروع',
  services: 'الخدمات',
  service_delivery: 'طرق التقديم',
  recommendation_needs: 'الاحتياجات',
  recommendation_rules: 'قواعد التوصية',
  consultants: 'المستشارون',
  training_programs: 'البرامج التدريبية',
  events: 'الفعاليات',
  workspace_packages: 'باقات مساحات العمل',
  digital_services: 'الخدمات الرقمية',
  branch_services: 'خدمات الفروع',
};

// Import order for display
const IMPORT_ORDER = [
  'programs', 'categories', 'cities', 'branches', 'services',
  'service_delivery', 'recommendation_needs', 'recommendation_rules',
  'consultants', 'training_programs', 'events', 'workspace_packages',
  'digital_services', 'branch_services'
];


function ExcelImportModal({ isOpen, onClose, onImportComplete }) {
  // Steps: 1=upload, 2=preview, 3=validation, 4=importing, 5=success
  const [currentStep, setCurrentStep] = useState(1);
  const [file, setFile] = useState(null);
  const [parseResult, setParseResult] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [clearExisting, setClearExisting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSheets, setExpandedSheets] = useState({});
  
  const STEPS = [
    { num: 1, label: 'رفع الملف', icon: Upload },
    { num: 2, label: 'معاينة الأوراق', icon: Eye },
    { num: 3, label: 'التحقق', icon: CheckCircle },
    { num: 4, label: 'الاستيراد', icon: Download },
    { num: 5, label: 'النتيجة', icon: Check },
  ];
  
  const getAuthHeaders = () => {
    const token = localStorage.getItem('hala_admin_token');
    return { 'Authorization': `Bearer ${token}` };
  };
  
  // Reset state when modal closes
  const handleClose = () => {
    setCurrentStep(1);
    setFile(null);
    setParseResult(null);
    setValidationResult(null);
    setImportResult(null);
    setClearExisting(false);
    setError(null);
    setExpandedSheets({});
    onClose();
  };
  
  // Handle file selection
  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    if (!selectedFile.name.toLowerCase().endsWith('.xlsx')) {
      setError('الرجاء اختيار ملف Excel بصيغة .xlsx');
      return;
    }
    
    setFile(selectedFile);
    setError(null);
    
    // Parse workbook
    await parseWorkbook(selectedFile);
  };
  
  // Step 1: Parse workbook
  const parseWorkbook = async (fileToUpload) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', fileToUpload);
      
      const response = await fetch(`${API}/excel/parse`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'فشل في قراءة الملف');
      }
      
      const result = await response.json();
      setParseResult(result);
      setCurrentStep(2);
      
    } catch (e) {
      setError(e.message || 'فشل في قراءة ملف Excel');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Step 2: Validate workbook
  const validateWorkbook = async () => {
    if (!file) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API}/excel/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'فشل في التحقق من البيانات');
      }
      
      const result = await response.json();
      setValidationResult(result);
      setCurrentStep(3);
      
    } catch (e) {
      setError(e.message || 'فشل في التحقق');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Step 3: Execute import
  const executeImport = async () => {
    if (!file) return;
    
    setIsLoading(true);
    setCurrentStep(4);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const url = `${API}/excel/import?clear_existing=${clearExisting}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'فشل في الاستيراد');
      }
      
      const result = await response.json();
      setImportResult(result);
      setCurrentStep(5);
      
      if (onImportComplete) {
        onImportComplete(result);
      }
      
    } catch (e) {
      setError(e.message || 'فشل في الاستيراد');
      setCurrentStep(3);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Toggle sheet expansion
  const toggleSheetExpand = (sheetName) => {
    setExpandedSheets(prev => ({
      ...prev,
      [sheetName]: !prev[sheetName]
    }));
  };
  
  // Render sheet status badge
  const renderStatusBadge = (status, statusAr) => {
    const config = STATUS_COLORS[status] || STATUS_COLORS.missing;
    const Icon = config.icon;
    
    return (
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        padding: '4px 12px', borderRadius: '20px',
        backgroundColor: config.bg, color: config.color,
        fontSize: '12px', fontWeight: 600
      }}>
        <Icon size={14} />
        {statusAr}
      </div>
    );
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      {/* Backdrop */}
      <div style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1000
      }} onClick={handleClose} />
      
      {/* Modal */}
      <div data-testid="excel-import-modal" style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '900px', maxWidth: '95vw', maxHeight: '90vh',
        backgroundColor: COLORS.white, borderRadius: '24px',
        boxShadow: '0 25px 80px rgba(0,0,0,0.3)', zIndex: 1001,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '24px 28px',
          borderBottom: `1px solid ${COLORS.border}`,
          background: `linear-gradient(135deg, ${COLORS.primary}08 0%, ${COLORS.white} 100%)`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '48px', height: '48px', borderRadius: '14px',
                backgroundColor: `${COLORS.success}15`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FileSpreadsheet size={26} color={COLORS.success} />
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: COLORS.text }}>
                  استيراد ملف Excel
                </h2>
                <p style={{ margin: '2px 0 0', fontSize: '14px', color: COLORS.textMuted }}>
                  استيراد بيانات متعددة الأوراق
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              style={{
                width: '40px', height: '40px', borderRadius: '12px', border: 'none',
                backgroundColor: COLORS.bg, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <X size={22} color={COLORS.textMuted} />
            </button>
          </div>
          
          {/* Step Progress */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {STEPS.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.num;
              const isCompleted = currentStep > step.num;
              const isLast = index === STEPS.length - 1;
              
              return (
                <div key={step.num} style={{ display: 'flex', alignItems: 'center', flex: isLast ? 'none' : 1 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '70px' }}>
                    <div style={{
                      width: '40px', height: '40px', borderRadius: '12px',
                      backgroundColor: isCompleted ? COLORS.success : isActive ? COLORS.primary : COLORS.bg,
                      border: `2px solid ${isCompleted ? COLORS.success : isActive ? COLORS.primary : COLORS.border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.3s ease',
                    }}>
                      {isCompleted ? (
                        <Check size={20} color={COLORS.white} strokeWidth={3} />
                      ) : (
                        <StepIcon size={18} color={isActive ? COLORS.white : COLORS.textMuted} />
                      )}
                    </div>
                    <span style={{
                      marginTop: '6px', fontSize: '11px', fontWeight: isActive ? 700 : 500,
                      color: isActive ? COLORS.primary : isCompleted ? COLORS.success : COLORS.textMuted,
                      textAlign: 'center', whiteSpace: 'nowrap',
                    }}>
                      {step.label}
                    </span>
                  </div>
                  
                  {!isLast && (
                    <div style={{
                      flex: 1, height: '2px',
                      backgroundColor: isCompleted ? COLORS.success : COLORS.border,
                      marginBottom: '22px', marginLeft: '8px', marginRight: '8px',
                    }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Loading Overlay */}
        {isLoading && (
          <div style={{
            padding: '16px 28px',
            backgroundColor: `${COLORS.primary}08`,
            borderBottom: `1px solid ${COLORS.border}`,
            display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <Loader2 size={20} color={COLORS.primary} style={{ animation: 'spin 1s linear infinite' }} />
            <span style={{ fontSize: '14px', fontWeight: 600, color: COLORS.text }}>
              {currentStep === 1 && 'جاري قراءة الملف...'}
              {currentStep === 2 && 'جاري التحقق من البيانات...'}
              {currentStep === 4 && 'جاري استيراد البيانات...'}
            </span>
          </div>
        )}
        
        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '28px' }}>
          {/* Error Message */}
          {error && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              padding: '14px 16px', marginBottom: '20px',
              backgroundColor: '#FEE2E2', border: '1px solid #FECACA', borderRadius: '12px',
            }}>
              <AlertCircle size={20} color={COLORS.error} />
              <span style={{ fontSize: '14px', color: '#991B1B' }}>{error}</span>
              <button onClick={() => setError(null)} style={{ marginRight: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={16} color="#991B1B" />
              </button>
            </div>
          )}
          
          {/* Step 1: Upload */}
          {currentStep === 1 && !isLoading && (
            <div>
              {/* Info Alert */}
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: '12px',
                padding: '16px', marginBottom: '24px',
                backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '12px',
              }}>
                <Info size={20} color={COLORS.info} style={{ flexShrink: 0, marginTop: '2px' }} />
                <div style={{ fontSize: '14px', color: '#1E40AF', lineHeight: 1.6 }}>
                  <strong>ملف Excel متعدد الأوراق</strong>
                  <p style={{ margin: '8px 0 0' }}>
                    الملف يجب أن يحتوي على أوراق باسماء محددة مثل: programs, categories, services, cities, branches, وغيرها.
                    سيتم تجاهل ورقة README تلقائياً.
                  </p>
                </div>
              </div>
              
              {/* File Drop Zone */}
              <label data-testid="excel-dropzone" style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                padding: '60px 40px',
                border: `3px dashed ${COLORS.success}40`,
                borderRadius: '20px',
                backgroundColor: `${COLORS.success}04`,
                cursor: 'pointer',
                transition: 'all 0.3s',
                marginBottom: '28px',
              }}>
                <div style={{
                  width: '88px', height: '88px', borderRadius: '50%',
                  backgroundColor: `${COLORS.success}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: '20px',
                  boxShadow: `0 8px 24px ${COLORS.success}20`,
                }}>
                  <FileSpreadsheet size={40} color={COLORS.success} />
                </div>
                <p style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 700, color: COLORS.text }}>
                  اختر ملف Excel أو اسحبه هنا
                </p>
                <p style={{ margin: 0, fontSize: '15px', color: COLORS.textMuted }}>
                  ملف .xlsx متعدد الأوراق
                </p>
                <input
                  data-testid="excel-file-input"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </label>
              
              {/* Expected Sheets */}
              <div style={{
                padding: '20px',
                backgroundColor: COLORS.bg,
                borderRadius: '16px',
                border: `1px solid ${COLORS.border}`,
              }}>
                <h4 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 600, color: COLORS.text }}>
                  الأوراق المتوقعة في الملف:
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {IMPORT_ORDER.map(sheet => (
                    <span key={sheet} style={{
                      padding: '6px 12px',
                      backgroundColor: COLORS.white,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '8px',
                      fontSize: '13px',
                      color: COLORS.textMuted,
                    }}>
                      {SHEET_LABELS[sheet] || sheet}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Step 2: Preview */}
          {currentStep === 2 && parseResult && !isLoading && (
            <div>
              {/* File Info */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '16px',
                padding: '18px',
                backgroundColor: `${COLORS.success}08`,
                borderRadius: '14px',
                marginBottom: '24px',
                border: `1px solid ${COLORS.success}25`,
              }}>
                <div style={{
                  width: '48px', height: '48px', borderRadius: '12px',
                  backgroundColor: `${COLORS.success}20`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <FileSpreadsheet size={24} color={COLORS.success} />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: COLORS.text }}>
                    {parseResult.filename}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
                    {parseResult.total_sheets} ورقة • {parseResult.detected_sheets?.length || 0} ورقة معروفة
                  </p>
                </div>
                <CheckCircle size={26} color={COLORS.success} />
              </div>
              
              {/* Detected Sheets */}
              <h4 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 700, color: COLORS.text }}>
                الأوراق المكتشفة ({parseResult.detected_sheets?.length || 0})
              </h4>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
                {IMPORT_ORDER.map(entity => {
                  const sheetInfo = parseResult.detected_sheets?.find(s => s.entity === entity);
                  const isDetected = !!sheetInfo;
                  
                  return (
                    <div key={entity} style={{
                      padding: '16px',
                      backgroundColor: isDetected ? COLORS.white : COLORS.bg,
                      border: `1px solid ${isDetected ? COLORS.success + '30' : COLORS.border}`,
                      borderRadius: '12px',
                      opacity: isDetected ? 1 : 0.6,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          {isDetected ? (
                            <CheckCircle size={20} color={COLORS.success} />
                          ) : (
                            <div style={{
                              width: '20px', height: '20px', borderRadius: '50%',
                              border: `2px dashed ${COLORS.border}`,
                            }} />
                          )}
                          <span style={{ fontSize: '14px', fontWeight: 600, color: COLORS.text }}>
                            {SHEET_LABELS[entity] || entity}
                          </span>
                        </div>
                        {isDetected && (
                          <span style={{
                            padding: '4px 12px',
                            backgroundColor: `${COLORS.primary}10`,
                            borderRadius: '20px',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: COLORS.primary,
                          }}>
                            {sheetInfo.row_count} سجل
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Unknown Sheets Warning */}
              {parseResult.unknown_sheets?.length > 0 && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: '12px',
                  padding: '14px 16px',
                  backgroundColor: '#FEF3C7',
                  border: '1px solid #FDE68A',
                  borderRadius: '12px',
                }}>
                  <AlertTriangle size={20} color={COLORS.warning} />
                  <div style={{ fontSize: '14px', color: '#92400E' }}>
                    <strong>أوراق غير معروفة:</strong> {parseResult.unknown_sheets.join(', ')}
                    <p style={{ margin: '4px 0 0', fontSize: '13px' }}>سيتم تجاهل هذه الأوراق</p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Step 3: Validation */}
          {currentStep === 3 && validationResult && !isLoading && (
            <div>
              {/* Summary */}
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px',
              }}>
                <div style={{
                  padding: '20px', textAlign: 'center',
                  backgroundColor: validationResult.can_import ? `${COLORS.success}10` : COLORS.bg,
                  borderRadius: '14px',
                  border: `1px solid ${validationResult.can_import ? COLORS.success + '30' : COLORS.border}`,
                }}>
                  <p style={{
                    margin: 0, fontSize: '28px', fontWeight: 800,
                    color: validationResult.can_import ? COLORS.success : COLORS.textMuted,
                  }}>
                    {validationResult.can_import ? 'جاهز' : 'غير جاهز'}
                  </p>
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
                    حالة الاستيراد
                  </p>
                </div>
                <div style={{
                  padding: '20px', textAlign: 'center',
                  backgroundColor: validationResult.total_errors > 0 ? `${COLORS.error}10` : COLORS.bg,
                  borderRadius: '14px',
                  border: `1px solid ${validationResult.total_errors > 0 ? COLORS.error + '30' : COLORS.border}`,
                }}>
                  <p style={{
                    margin: 0, fontSize: '28px', fontWeight: 800,
                    color: validationResult.total_errors > 0 ? COLORS.error : COLORS.success,
                  }}>
                    {validationResult.total_errors}
                  </p>
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
                    أخطاء
                  </p>
                </div>
                <div style={{
                  padding: '20px', textAlign: 'center',
                  backgroundColor: validationResult.total_warnings > 0 ? `${COLORS.warning}10` : COLORS.bg,
                  borderRadius: '14px',
                  border: `1px solid ${validationResult.total_warnings > 0 ? COLORS.warning + '30' : COLORS.border}`,
                }}>
                  <p style={{
                    margin: 0, fontSize: '28px', fontWeight: 800,
                    color: validationResult.total_warnings > 0 ? COLORS.warning : COLORS.textMuted,
                  }}>
                    {validationResult.total_warnings}
                  </p>
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
                    تنبيهات
                  </p>
                </div>
              </div>
              
              {/* Sheet Statuses */}
              <h4 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 700, color: COLORS.text }}>
                حالة الأوراق
              </h4>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
                {IMPORT_ORDER.map(entity => {
                  const status = validationResult.sheet_statuses?.[entity];
                  if (!status) return null;
                  
                  const isExpanded = expandedSheets[entity];
                  const hasErrors = status.errors?.length > 0;
                  const hasWarnings = status.warnings?.length > 0;
                  
                  return (
                    <div key={entity} style={{
                      backgroundColor: COLORS.white,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '12px',
                      overflow: 'hidden',
                    }}>
                      <div
                        onClick={() => (hasErrors || hasWarnings || status.preview?.length) && toggleSheetExpand(entity)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '14px 16px',
                          cursor: (hasErrors || hasWarnings || status.preview?.length) ? 'pointer' : 'default',
                          backgroundColor: status.status === 'error' ? '#FEF2F2' : status.status === 'warning' ? '#FFFBEB' : COLORS.white,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          {(hasErrors || hasWarnings || status.preview?.length) ? (
                            isExpanded ? <ChevronDown size={18} color={COLORS.textMuted} /> : <ChevronRight size={18} color={COLORS.textMuted} />
                          ) : (
                            <div style={{ width: '18px' }} />
                          )}
                          <span style={{ fontSize: '14px', fontWeight: 600, color: COLORS.text }}>
                            {SHEET_LABELS[entity] || entity}
                          </span>
                          <span style={{ fontSize: '13px', color: COLORS.textMuted }}>
                            ({status.row_count} سجل)
                          </span>
                        </div>
                        {renderStatusBadge(status.status, status.status_ar)}
                      </div>
                      
                      {/* Expanded Content */}
                      {isExpanded && (
                        <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${COLORS.border}` }}>
                          {/* Errors */}
                          {hasErrors && (
                            <div style={{ marginTop: '12px' }}>
                              <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600, color: COLORS.error }}>
                                الأخطاء:
                              </p>
                              <ul style={{ margin: 0, paddingRight: '20px' }}>
                                {status.errors.slice(0, 5).map((err, i) => (
                                  <li key={i} style={{ fontSize: '13px', color: '#991B1B', marginBottom: '4px' }}>
                                    {err.message}
                                  </li>
                                ))}
                                {status.errors.length > 5 && (
                                  <li style={{ fontSize: '13px', color: COLORS.textMuted }}>
                                    و {status.errors.length - 5} أخطاء أخرى...
                                  </li>
                                )}
                              </ul>
                            </div>
                          )}
                          
                          {/* Warnings */}
                          {hasWarnings && (
                            <div style={{ marginTop: '12px' }}>
                              <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600, color: COLORS.warning }}>
                                التنبيهات:
                              </p>
                              <ul style={{ margin: 0, paddingRight: '20px' }}>
                                {status.warnings.slice(0, 3).map((warn, i) => (
                                  <li key={i} style={{ fontSize: '13px', color: '#92400E', marginBottom: '4px' }}>
                                    {warn.message}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* Preview */}
                          {status.preview?.length > 0 && !hasErrors && (
                            <div style={{ marginTop: '12px' }}>
                              <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>
                                معاينة (أول 5 سجلات):
                              </p>
                              <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ backgroundColor: COLORS.bg }}>
                                      {Object.keys(status.preview[0]).filter(k => !k.startsWith('_')).slice(0, 4).map(key => (
                                        <th key={key} style={{ padding: '8px', textAlign: 'right', fontWeight: 600, borderBottom: `1px solid ${COLORS.border}` }}>
                                          {key}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {status.preview.slice(0, 3).map((row, i) => (
                                      <tr key={i}>
                                        {Object.entries(row).filter(([k]) => !k.startsWith('_')).slice(0, 4).map(([key, val], j) => (
                                          <td key={j} style={{ padding: '8px', borderBottom: `1px solid ${COLORS.border}` }}>
                                            {String(val || '-').substring(0, 30)}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Clear Existing Option */}
              {validationResult.can_import && (
                <div style={{
                  padding: '16px',
                  backgroundColor: clearExisting ? `${COLORS.error}08` : COLORS.bg,
                  border: `1px solid ${clearExisting ? COLORS.error + '30' : COLORS.border}`,
                  borderRadius: '12px',
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={clearExisting}
                      onChange={(e) => setClearExisting(e.target.checked)}
                      style={{ width: '18px', height: '18px', accentColor: COLORS.error }}
                    />
                    <div>
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: clearExisting ? COLORS.error : COLORS.text }}>
                        حذف البيانات الحالية قبل الاستيراد
                      </p>
                      <p style={{ margin: '2px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
                        سيتم حذف جميع البيانات الموجودة واستبدالها بالبيانات الجديدة
                      </p>
                    </div>
                  </label>
                </div>
              )}
            </div>
          )}
          
          {/* Step 4: Importing */}
          {currentStep === 4 && (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{
                width: '100px', height: '100px', borderRadius: '50%',
                backgroundColor: `${COLORS.primary}12`,
                margin: '0 auto 28px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Loader2 size={48} color={COLORS.primary} style={{ animation: 'spin 1s linear infinite' }} />
              </div>
              <h3 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: 700, color: COLORS.text }}>
                جاري استيراد البيانات
              </h3>
              <p style={{ margin: 0, fontSize: '16px', color: COLORS.textMuted }}>
                يتم إدخال البيانات بالترتيب الصحيح...
              </p>
            </div>
          )}
          
          {/* Step 5: Success */}
          {currentStep === 5 && importResult && (
            <div>
              {/* Success Header */}
              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <div style={{
                  width: '100px', height: '100px', borderRadius: '50%',
                  backgroundColor: `${COLORS.success}12`,
                  margin: '0 auto 20px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: `0 8px 24px ${COLORS.success}20`,
                }}>
                  <CheckCircle size={52} color={COLORS.success} />
                </div>
                <h3 style={{ margin: '0 0 8px', fontSize: '26px', fontWeight: 800, color: COLORS.text }}>
                  تم استيراد البيانات بنجاح!
                </h3>
              </div>
              
              {/* Import Stats */}
              <h4 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 700, color: COLORS.text }}>
                ملخص الاستيراد:
              </h4>
              
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '24px',
              }}>
                {IMPORT_ORDER.map(entity => {
                  const stat = importResult.stats?.[entity];
                  if (!stat || (stat.imported === 0 && stat.skipped === 0)) return null;
                  
                  return (
                    <div key={entity} style={{
                      padding: '14px',
                      backgroundColor: stat.imported > 0 ? `${COLORS.success}08` : COLORS.bg,
                      borderRadius: '12px',
                      border: `1px solid ${stat.imported > 0 ? COLORS.success + '25' : COLORS.border}`,
                    }}>
                      <p style={{ margin: 0, fontSize: '13px', color: COLORS.textMuted }}>
                        {SHEET_LABELS[entity] || entity}
                      </p>
                      <p style={{ margin: '4px 0 0', fontSize: '20px', fontWeight: 700, color: COLORS.success }}>
                        {stat.imported}
                        {stat.skipped > 0 && (
                          <span style={{ fontSize: '13px', color: COLORS.textMuted, fontWeight: 400 }}>
                            {' '}({stat.skipped} تخطي)
                          </span>
                        )}
                      </p>
                    </div>
                  );
                })}
              </div>
              
              {/* Verification */}
              {importResult.verification && (
                <div style={{
                  padding: '16px',
                  backgroundColor: importResult.verification.has_issues ? `${COLORS.warning}08` : `${COLORS.success}08`,
                  borderRadius: '12px',
                  border: `1px solid ${importResult.verification.has_issues ? COLORS.warning + '25' : COLORS.success + '25'}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                    {importResult.verification.has_issues ? (
                      <AlertTriangle size={20} color={COLORS.warning} />
                    ) : (
                      <CheckCircle size={20} color={COLORS.success} />
                    )}
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: COLORS.text }}>
                      {importResult.verification.has_issues ? 'تم الاستيراد ولكن توجد عناصر تحتاج مراجعة' : 'تم التحقق بنجاح'}
                    </p>
                  </div>
                  
                  {/* Counts */}
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px', color: COLORS.textMuted }}>
                    <span>البرامج: {importResult.verification.counts?.programs || 0}</span>
                    <span>التصنيفات: {importResult.verification.counts?.categories || 0}</span>
                    <span>الخدمات: {importResult.verification.counts?.services || 0}</span>
                    <span>المدن: {importResult.verification.counts?.cities || 0}</span>
                    <span>الفروع: {importResult.verification.counts?.branches || 0}</span>
                  </div>
                  
                  {/* Issues */}
                  {importResult.verification.issues?.length > 0 && (
                    <ul style={{ margin: '12px 0 0', paddingRight: '20px' }}>
                      {importResult.verification.issues.map((issue, i) => (
                        <li key={i} style={{ fontSize: '13px', color: COLORS.warning, marginBottom: '4px' }}>
                          {issue}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div style={{
          padding: '20px 28px',
          borderTop: `1px solid ${COLORS.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          backgroundColor: COLORS.bg,
        }}>
          {/* Left Actions */}
          <div>
            {currentStep === 1 && (
              <button
                onClick={handleClose}
                style={{
                  padding: '10px 20px', borderRadius: '10px', border: 'none',
                  backgroundColor: 'transparent', color: COLORS.textMuted,
                  fontSize: '14px', fontWeight: 500, cursor: 'pointer',
                }}
              >
                إلغاء
              </button>
            )}
            {currentStep > 1 && currentStep < 5 && !isLoading && (
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '10px 20px', borderRadius: '10px',
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.white, color: COLORS.text,
                  fontSize: '14px', fontWeight: 500, cursor: 'pointer',
                }}
              >
                <ArrowRight size={18} />
                السابق
              </button>
            )}
          </div>
          
          {/* Right Actions */}
          <div style={{ display: 'flex', gap: '12px' }}>
            {currentStep === 2 && !isLoading && (
              <button
                onClick={validateWorkbook}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '12px 24px', borderRadius: '12px', border: 'none',
                  backgroundColor: COLORS.primary, color: COLORS.white,
                  fontSize: '15px', fontWeight: 600, cursor: 'pointer',
                }}
              >
                التالي - التحقق من البيانات
                <ArrowLeft size={18} />
              </button>
            )}
            
            {currentStep === 3 && !isLoading && validationResult?.can_import && (
              <button
                onClick={executeImport}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '12px 24px', borderRadius: '12px', border: 'none',
                  backgroundColor: COLORS.success, color: COLORS.white,
                  fontSize: '15px', fontWeight: 600, cursor: 'pointer',
                }}
              >
                <Download size={18} />
                استيراد البيانات
              </button>
            )}
            
            {currentStep === 3 && !isLoading && !validationResult?.can_import && (
              <button
                disabled
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '12px 24px', borderRadius: '12px', border: 'none',
                  backgroundColor: COLORS.border, color: COLORS.textMuted,
                  fontSize: '15px', fontWeight: 600, cursor: 'not-allowed',
                }}
              >
                يجب إصلاح الأخطاء أولاً
              </button>
            )}
            
            {currentStep === 5 && (
              <button
                onClick={handleClose}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '12px 24px', borderRadius: '12px', border: 'none',
                  backgroundColor: COLORS.success, color: COLORS.white,
                  fontSize: '15px', fontWeight: 600, cursor: 'pointer',
                }}
              >
                <CheckCircle size={18} />
                إغلاق
              </button>
            )}
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}

export default ExcelImportModal;
